import React, { useContext } from 'react';
import styled from 'styled-components';
import { StringParam, useQueryParam } from 'use-query-params';
import { allMetricDescriptions, MetricKind } from '@/webapi/models';
import { formatEnum } from '@/utils/types';
import { Tooltip } from '@/components/tooltip';
import { DefaultTypography } from '@/components/typography';
import {
  SessionsWithImpressionsTooltipText,
  UsersTooltipText,
} from '@/features/details/sessions-type-chip';
import { SummaryContext } from '@/features/details/summary/context';
import { getCustomExpEventName } from '@/features/details/summary/see-more-reports-list';

export function useMetricName(
  value: MetricKind,
  isLarge: boolean,
  audience: string,
  sessionType: string,
) {
  const ctx = useContext(SummaryContext);
  let title = formatEnum(value, true);
  if (value === MetricKind.AVG_ORDER_VALUE && !isLarge) {
    title = `AOV`;
  }
  if (value === MetricKind.PER_SESSION_VALUE && !isLarge) {
    title = `PSV`;
  }
  if (value === MetricKind.CONVERSION_RATE && !isLarge) {
    title = `CR`;
  }
  if (value === MetricKind.AVG_SESSION_DURATION && !isLarge) {
    title = `ASD`;
  }
  if (value === MetricKind.ITEMS_PER_PURCHASE && !isLarge) {
    title = `IPP`;
  }
  if (value === MetricKind.PRODUCT_DETAILS_VIEWS_RATE && !isLarge) {
    title = `PDV`;
  }
  if (value === MetricKind.PAGES_PER_SESSION && !isLarge) {
    title = `PPS`;
  }
  if (value === MetricKind.CTR) {
    title = `CTR`;
  }
  if (!audience && value === MetricKind.REVENUE && isLarge) {
    title = `Revenue through experience`;
  }
  const res = handleSessionType(value, title, sessionType);
  const { tooltipText, tooltipTitle } = res;
  title = ctx?.getCustomMetricName(value) || res.title;
  if (value?.toString()?.startsWith(`EVENT_`)) {
    title = getCustomExpEventName(value, ctx.elementTracking);
    let tooltip = `custom event: "${title}"`;
    if (value.toString().endsWith(`_RATE`)) {
      tooltip = `${tooltip}, divided by sessions`;
    }
    return { title, tooltipText: tooltip, tooltipTitle: title };
  }

  return { title, tooltipText, tooltipTitle };
}

export function Header({
  value,
  isLarge,
}: {
  value: MetricKind;
  isLarge: boolean;
}) {
  const [audience] = useQueryParam(`aud`);
  const [sessionType] = useQueryParam<string>(`st`, StringParam);
  const { title, tooltipText, tooltipTitle } = useMetricName(
    value,
    isLarge,
    audience as string,
    sessionType,
  );

  return (
    <SummaryHeader isLarge={isLarge}>
      <span>{title}</span>
      <Tooltip zIndex={0} title={tooltipTitle} text={tooltipText} />
    </SummaryHeader>
  );
}

export const SummaryHeader = styled(DefaultTypography)`
  && {
    display: flex;
    align-items: center;
    text-transform: capitalize;
    color: #8fa3ad;
    width: ${(p: { isLarge: boolean }) => (p?.isLarge ? `170px` : `110px`)};
    font-size: ${(p: { isLarge: boolean }) =>
      p?.isLarge ? `1.45rem` : `1.2rem`};
    font-weight: 500;
  }
`;

function isUserSessType(sessionType: string, value: MetricKind) {
  return sessionType === `users` && value === MetricKind.SESSIONS;
}

function isSessWithImp(sessionType: string, value: MetricKind) {
  return sessionType === `sessions` && value === MetricKind.SESSIONS;
}

function handleSessionType(
  value: MetricKind,
  title: string,
  sessionType: string,
) {
  let tooltipText = allMetricDescriptions.get(value);
  const isUserSessionType = isUserSessType(sessionType, value);
  const isSessionWithImpression = isSessWithImp(sessionType, value);

  if (isUserSessionType) {
    title = `users`;
    tooltipText = UsersTooltipText;
  } else if (isSessionWithImpression) {
    tooltipText = SessionsWithImpressionsTooltipText;
  }
  if (sessionType === `users` && value !== MetricKind.AVG_SESSION_DURATION) {
    tooltipText = tooltipText
      ?.replaceAll(`sessions`, `users`)
      ?.replaceAll(`session`, `user`);
  }
  const tooltipTitle =
    value === MetricKind.SESSIONS && isSessionWithImpression
      ? `Sessions with impression`
      : title;
  return { tooltipText, title, tooltipTitle };
}
